import { useState } from "react";
import { Box, Button, Chip, Collapse, Paper, Stack, Typography, useTheme } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryIcon from "@mui/icons-material/History";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { FoodsTooltip } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { Loading } from "src/components/utils/Loading";
import { useGetOneSsq, type Version } from "src/api/sure-api/ssq/useGetOneSsq";
import {
    SsqVersionAssignment,
    useGetAllSsqVersionAssignments,
} from "src/api/client-api/questionnaire/useGetAllSsqVersionAssignments";
import { type Campaign, useGetAllCampaigns } from "src/api/client-api/useCampaign";
import { formatDate } from "src/utils/formatting";
import { useCreateSsqVersionAssignment } from "src/api/client-api/questionnaire/useCreateSsqVersionAssignment";
import { useUpdateSsqVersionAssignment } from "src/api/client-api/questionnaire/useUpdateSsqVersionAssignment";
import { HTMLBox } from "src/components/HTMLBox/HTMLBox";
import { ChangeVersionDialog } from "./ChangeVersionDialog";

const useStatusConfig = () => {
    const { t } = useTranslation();

    const config: Record<number, { label: string; color: "default" | "success" | "warning" }> = {
        0: { label: t("settings.questionnaires.status_draft", "Draft"), color: "default" },
        1: { label: t("settings.questionnaires.status_published", "Published"), color: "success" },
        2: { label: t("settings.questionnaires.status_archived", "Archived"), color: "warning" },
    };

    return config;
};

const VersionLabel = ({ version }: { version: Version }) => {
    const statusConfig = useStatusConfig();
    const config = statusConfig[version.status];

    return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
};

type MajorVersionRowProps = {
    version: Version;
    isEven: boolean;
    assignments: SsqVersionAssignment[];
};

const MajorVersionRow = ({ version, isEven, assignments }: MajorVersionRowProps) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const dateStr = version.updatedAt ?? version.createdAt;
    const releaseNotes = version.releaseNotes?.trim();

    return (
        <Box
            sx={{
                bgcolor: isEven ? "white" : "#fefcfb",
                borderTop: "1px solid",
                borderColor: "divider",
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                sx={{ minHeight: 56, cursor: "pointer", "&:hover": { bgcolor: "#fafaf9" } }}
                onClick={() => setExpanded((v) => !v)}
            >
                <Box px={1} flexShrink={0} width={40} display="flex" alignItems="center" justifyContent="center">
                    {expanded ?
                        <KeyboardArrowUpIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                    :   <KeyboardArrowDownIcon sx={{ fontSize: 15, color: "text.secondary" }} />}
                </Box>

                <Box px={2} flexShrink={0} width={140}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                flexShrink: 0,
                            }}
                        />
                        <Typography variant="body-bold">v{version.versionMajor}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                        {version.versionMinor} {t("settings.questionnaires.patches", "patch(es)")}
                    </Typography>
                </Box>

                <Box px={2} flexShrink={0} width={220}>
                    <Stack gap={0.25}>
                        <Typography variant="body-semibold">
                            v{version.versionMajor}.{version.versionMinor}
                        </Typography>
                        {dateStr && (
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(dateStr)}
                            </Typography>
                        )}
                    </Stack>
                </Box>

                <Box px={2} flexShrink={0} width={120}>
                    <VersionLabel version={version} />
                </Box>

                <Box px={2} flex={1}>
                    <Typography variant="body" color="text.secondary">
                        {[...new Map(assignments.map((a) => [a.campaign.id, a])).values()].map((assignment) => {
                            return (
                                <Chip
                                    key={assignment.id}
                                    label={assignment.campaign.label}
                                    color="secondary"
                                    size="small"
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                />
                            );
                        })}
                    </Typography>
                </Box>
            </Box>

            <Collapse in={expanded} unmountOnExit>
                <Box
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: "1px dashed",
                        borderColor: "divider",
                        bgcolor: "#fafaf9",
                        ml: "40px",
                    }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            mb: 0.75,
                            display: "block",
                        }}
                    >
                        {t("settings.questionnaires.release_notes", "Release notes")} · v{version.versionMajor}.
                        {version.versionMinor}
                    </Typography>
                    {releaseNotes ?
                        <HTMLBox value={releaseNotes} seeMoreColor="#398BDB" />
                    :   <Typography variant="body" color="text.primary" sx={{ lineHeight: 1.75 }}>
                            {t("settings.questionnaires.no_release_notes", "No release notes available.")}
                        </Typography>
                    }
                </Box>
            </Collapse>
        </Box>
    );
};

type CampaignAssignmentRowProps = {
    campaign: Campaign;
    version: Version;
    currentVersion: number;
    assignment?: SsqVersionAssignment;
    isEven: boolean;
    onChangeVersion: () => void;
};

const CampaignAssignmentRow = (props: CampaignAssignmentRowProps) => {
    const { campaign, version, currentVersion, assignment, isEven, onChangeVersion } = props;
    const { t } = useTranslation();
    const statusConfig = useStatusConfig();

    return (
        <Box
            display="flex"
            alignItems="center"
            sx={{
                bgcolor: isEven ? "white" : "#fefcfb",
                borderTop: "1px solid",
                borderColor: "divider",
                minHeight: 56,
            }}
        >
            <Box px={2.5} py={1.5} flexShrink={0} width={120}>
                <Typography variant="body-semibold">{campaign.label}</Typography>
            </Box>

            <Box px={2} py={1.5} flexShrink={0} width={160}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="body-bold">
                        v{version.versionMajor}.{version.versionMinor}
                    </Typography>
                    {version.id === currentVersion ?
                        <Chip
                            label={t("settings.questionnaires.up_to_date", "Up to date")}
                            color="success"
                            variant="outlined"
                            size="small"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                    :   <Chip
                            label={t("settings.questionnaires.outdated", "Outdated")}
                            color="warning"
                            variant="outlined"
                            size="small"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                    }
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    {statusConfig[version.status].label}
                </Typography>
            </Box>

            <Box px={2} py={1.5} flexShrink={0} width={110}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="body-bold">{assignment?.answerCount ?? "--"}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    {t("settings.questionnaires.answers", "answers")}
                </Typography>
            </Box>

            <Box px={2} py={1.5} flexShrink={0} width={140}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="body-bold">{assignment?.ssqPostCount ?? "--"}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    {t("settings.questionnaires.posts", "posts")}
                </Typography>
            </Box>

            <Box px={2} py={1.5} flexShrink={0} width={210}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="body-bold">{assignment?.relatedActionPlansCount ?? "--"}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    {t("settings.questionnaires.related_action_plans", "related action plans")}
                </Typography>
            </Box>

            <Box px={2} py={1.5} flexShrink={0} width={300}>
                {!assignment && (
                    <Chip
                        label={t("settings.questionnaires.auto_assigned", "Auto assigned - no manual assignment")}
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                )}
            </Box>

            <Box flex={1} px={2} py={1.5} display="flex" alignItems="center" justifyContent="flex-end">
                <FoodsTooltip
                    title={t("settings.questionnaires.change_version_tooltip", "Change version for this campaign")}
                    placement="top"
                    width="unset"
                >
                    <Button
                        startIcon={<SwapHorizIcon sx={{ fontSize: "15px !important" }} />}
                        onClick={onChangeVersion}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: "none", borderRadius: "999px" }}
                    >
                        {t("settings.questionnaires.change", "Change")}
                    </Button>
                </FoodsTooltip>
            </Box>
        </Box>
    );
};

export const Questionnaire = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { id } = useParams<{ id: string }>();

    const ssqId = id ? Number(id) : undefined;
    const { ssq, isReady } = useGetOneSsq(ssqId);
    const { assignments, isReady: assignmentsReady } = useGetAllSsqVersionAssignments(ssqId);
    const { campaigns, isSuccess: campaignsLoaded } = useGetAllCampaigns();

    const currentYear = new Date().getFullYear();
    const campaignsToSelectFrom = campaigns.flatMap((c) => {
        const labelAsYear = Number(c.label);
        if (currentYear >= labelAsYear - 1) {
            return [c];
        }

        return [];
    });

    const returnUrl = searchParams.get("returnUrl") ?? "/settings/questionnaires";

    const [changeCampaignId, setChangeCampaignId] = useState<number | null>(null);
    const createAssignment = useCreateSsqVersionAssignment();
    const updateAssignment = useUpdateSsqVersionAssignment();

    if (!isReady || !campaignsLoaded || !assignmentsReady) {
        return <Loading />;
    }

    if (!ssq) {
        return (
            <Typography variant="h3">{t("settings.questionnaires.not_found", "Questionnaire not found")}</Typography>
        );
    }

    const publishedVersions = ssq.versions.filter((v) => v.status === 1 || v.status === 2);

    const assignmentsByVersion = assignments.reduce<Record<number, SsqVersionAssignment[]>>((acc, a) => {
        const cid = a.ssqVersionId;
        if (!acc[cid]) {
            acc[cid] = [];
        }
        acc[cid].push(a);

        return acc;
    }, {});

    const assignmentsByCampaign = assignments.reduce<Record<number, SsqVersionAssignment>>((acc, a) => {
        const cid = a.campaign.id;
        acc[cid] = a;

        return acc;
    }, {});

    const versionsByVersionId = publishedVersions.reduce<Record<number, Version>>((acc, version) => {
        const vid = version.id;
        acc[vid] = version;

        return acc;
    }, {});

    const versionByCampaignId = assignments.reduce<Record<number, Version>>((acc, assignment) => {
        acc[assignment.campaign.id] = versionsByVersionId[assignment.ssqVersionId];
        return acc;
    }, {});

    const currentVersion = publishedVersions[0].id;
    const linkedEntities = assignments[0]?.linkedEntityNames ?? [];

    const changeAssignment = changeCampaignId !== null ? assignmentsByCampaign[changeCampaignId] : undefined;

    return (
        <Box sx={{ padding: "2rem" }}>
            <Button
                startIcon={<ArrowBackIcon sx={{ fontSize: "14px !important" }} />}
                onClick={() => navigate(returnUrl)}
                sx={{ textTransform: "none", mb: 3, p: 0, minWidth: 0, color: "text.secondary" }}
            >
                {t("settings.questionnaires.back_to_list", "Back to questionnaires")}
            </Button>

            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={3} mb={3}>
                <Box>
                    <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {ssq.title}
                    </Typography>
                    <Typography variant="body" color="text.secondary">
                        {publishedVersions.length} {t("settings.questionnaires.version_count", "version(s)")}
                    </Typography>
                    &nbsp;-&nbsp;
                    <Typography variant="body" color="text.secondary">
                        {linkedEntities.length} {t("settings.questionnaires.linked_entities_count", "linked entities")}
                    </Typography>
                </Box>
            </Stack>

            <Box mb={3}>
                <Stack direction="row" alignItems="center" gap={1.5} mb={2}>
                    <HistoryIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body-bold">
                        {t("settings.questionnaires.version_history", "Version history")}
                    </Typography>
                </Stack>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: "16px",
                        border: `1px solid ${theme.custom.grey[500]}`,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                            height: 40,
                            bgcolor: "#fefcfb",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Box px={1} flexShrink={0} width={40} />
                        <Box px={2} flexShrink={0} width={140}>
                            <Typography variant="caption" color="text.secondary">
                                {t("settings.questionnaires.col_version", "Version")}
                            </Typography>
                        </Box>
                        <Box px={2} flexShrink={0} width={220}>
                            <Typography variant="caption" color="text.secondary">
                                {t("settings.questionnaires.col_latest_update", "Latest update")}
                            </Typography>
                        </Box>
                        <Box px={2} flexShrink={0} width={120}>
                            <Typography variant="caption" color="text.secondary">
                                {t("settings.questionnaires.col_status", "Status")}
                            </Typography>
                        </Box>
                        <Box px={2} flex={1}>
                            <Typography variant="caption" color="text.secondary">
                                {t("settings.questionnaires.col_campaigns", "Campaigns")}
                            </Typography>
                        </Box>
                    </Box>

                    {publishedVersions.map((version: Version, idx) => (
                        <MajorVersionRow
                            key={idx}
                            version={version}
                            isEven={idx % 2 === 0}
                            assignments={assignmentsByVersion[version.id] ?? []}
                        />
                    ))}
                </Paper>
            </Box>

            <Box mb={3}>
                <Stack direction="row" alignItems="center" gap={1.5} mb={2}>
                    <EditCalendarIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body-bold">
                        {t("settings.questionnaires.campaign_assignments", "Campaign assignments")}
                    </Typography>
                </Stack>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: "16px",
                        border: `1px solid ${theme.custom.grey[500]}`,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                            height: 44,
                            bgcolor: "#fefcfb",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Box px={2.5} flexShrink={0} width={120}>
                            <Typography variant="caption" color="text.secondary">
                                {t("settings.questionnaires.col_campaign", "Campaign")}
                            </Typography>
                        </Box>
                        <Box px={2} flexShrink={0} width={160}>
                            <Typography variant="caption" color="text.secondary">
                                {t("settings.questionnaires.col_assigned_version", "Assigned version")}
                            </Typography>
                        </Box>
                        <Box px={2} flexShrink={0} width={110}>
                            <Typography variant="caption" color="text.secondary">
                                {t("settings.questionnaires.col_answers", "Answers")}
                            </Typography>
                        </Box>
                        <Box px={2} flexShrink={0} width={140}>
                            <Typography variant="caption" color="text.secondary">
                                {t("settings.questionnaires.col_posts", "Posts")}
                            </Typography>
                        </Box>
                        <Box px={2} flexShrink={0} width={210}>
                            <Typography variant="caption" color="text.secondary">
                                {t("settings.questionnaires.col_related_action_plans", "Action plans")}
                            </Typography>
                        </Box>
                        <Box px={2} flexShrink={0} width={140}>
                            <Typography variant="caption" color="text.secondary">
                                {t("settings.questionnaires.col_notes", "Notes")}
                            </Typography>
                        </Box>
                        <Box flex={1} px={2} />
                    </Box>

                    {[...campaignsToSelectFrom]
                        .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())
                        .map((campaign: Campaign, idx: number) => {
                            const version = versionByCampaignId[campaign.id] ?? publishedVersions[0];
                            if (!version) {
                                return null;
                            }

                            return (
                                <CampaignAssignmentRow
                                    key={idx}
                                    campaign={campaign}
                                    version={version}
                                    assignment={assignmentsByCampaign[campaign.id]}
                                    currentVersion={currentVersion}
                                    isEven={idx % 2 === 0}
                                    onChangeVersion={() => setChangeCampaignId(campaign.id)}
                                />
                            );
                        })}
                </Paper>
            </Box>

            <Box>
                <Typography variant="body-bold" sx={{ mb: 1.25 }}>
                    {t("settings.questionnaires.linked_entities_title", "Linked entities")}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {linkedEntities.length > 0 ?
                        linkedEntities.map((name, index) => <Chip key={index} label={name} />)
                    :   <Typography variant="body" color="text.secondary">
                            {t("settings.questionnaires.no_linked_entities", "No linked entities available.")}
                        </Typography>
                    }
                </Stack>
            </Box>

            {changeCampaignId !== null && (
                <ChangeVersionDialog
                    onClose={() => setChangeCampaignId(null)}
                    isApplying={createAssignment.isLoading || updateAssignment.isLoading}
                    onApply={(newVersionId: number) => {
                        const onSettled = () => setChangeCampaignId(null);
                        if (changeAssignment) {
                            updateAssignment.mutate(
                                { id: changeAssignment.id, ssqVersionId: newVersionId },
                                { onSettled },
                            );
                        } else {
                            createAssignment.mutate(
                                {
                                    ssqId: ssq.id,
                                    ssqVersionId: newVersionId,
                                    campaign: `/api/campaigns/${changeCampaignId}`,
                                },
                                { onSettled },
                            );
                        }
                    }}
                    ssq={ssq}
                    assignment={changeAssignment}
                />
            )}
        </Box>
    );
};
